import {Component, Inject, Input, OnChanges, OnDestroy} from '@angular/core';
import {LegendData, OverviewChartOptions} from '../../models/overview-models';
import {MetricType} from '../../../shared/metric-types';
import {benchmarkChangedTo} from '../../../store/actions';
import {AnalyticsService, AnalyticsServiceToken} from '../../../analytics/analytics.service';
import {NgRedux, select} from '@angular-redux/store';
import {IAppState} from '../../../store/IAppState';
import {Observable, Subscription} from 'rxjs';
import {DropdownOption, EmDimension} from '../../../shared/models';
import {updateCommunityBenchmarkAlertStatus} from '../../../shared/reducer-helper';
import {BenchmarkType} from '../../../shared/benchmark-types';

@Component({
  selector: 'app-overview-tile',
  templateUrl: './overview-tile.component.html',
  styleUrls: ['./overview-tile.component.scss']
})
export class OverviewTileComponent implements OnChanges, OnDestroy {
  @Input() metricType: MetricType;
  @Input() title: string;
  @Input() selectedDateRange: string;
  @Input() previousDateRange: string;
  @Input() comparedTo: string;
  @Input() legendData: LegendData[];
  @Input() overviewChartOptions: OverviewChartOptions;
  @Input() showEmOverviewGraph: boolean;
  @Input() showOverviewZipTile: boolean;
  @Input() showProgressBar: boolean;
  @Input() varianceSubtractFrom: string;
  @Input() varianceSubtract: string;
  @Input() variance: string;
  @Input() varianceClass: string;
  @Input() previousYearDifference: string;
  @Input() previousYearDifferenceClass: string;
  @Input() isWrvus: boolean;
  @Input() isCfteAdjusted: boolean;
  @Input() fromPdfExport = false;
  @Input() emMetrics: EmDimension[];
  @Input() benchmarkLabel: string;
  @Input() hasCommunityBenchmarks: boolean;
  @Input() hasAcademicBenchmarks: boolean;
  @Input() telehealthBenchmarksEnabled = false;

  hasBenchmarks = true;
  benchMarkType: BenchmarkType;
  benchMarkDropDownClass: string;
  legendDataTop?: LegendData[];
  legendDataBottom?: LegendData[];
  MetricType = MetricType;

  @select(['benchmark'])
  private readonly benchmarkType$: Observable<BenchmarkType>;

  // noinspection JSUnusedLocalSymbols (this actually is used in the html)
  benchmarkTypes: DropdownOption[] = [
    {value: BenchmarkType.Mean, name: 'benchmark mean'},
    {value: BenchmarkType.Percentile25th, name: '25th percentile benchmark'},
    {value: BenchmarkType.Percentile50th, name: '50th percentile benchmark'},
    {value: BenchmarkType.Percentile65th, name: '65th percentile benchmark'},
    {value: BenchmarkType.Percentile75th, name: '75th percentile benchmark'},
    {value: BenchmarkType.Percentile90th, name: '90th percentile benchmark'}
  ];
  private subscription: Subscription;

  constructor(
    @Inject(AnalyticsServiceToken) private analyticsService: AnalyticsService,
    private ngRedux: NgRedux<IAppState>
  ) {
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  ngOnChanges(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.subscription = this.benchmarkType$.subscribe((benchmarkTypeFromStore: BenchmarkType) => {
      this.benchMarkType = this.metricType === MetricType.NewPatientVisits &&
      benchmarkTypeFromStore === BenchmarkType.Percentile65th ? BenchmarkType.Mean : benchmarkTypeFromStore;
      this.setBenchmarkDropDownClass(this.benchMarkType);
      if (this.legendData) {
        this.legendDataBottom = [];
        this.legendDataTop = [];
        for (let i = 0; i < this.legendData.length; i++) {
          if (i < 2) {
            this.legendDataTop.push(this.legendData[i]);
          } else {
            this.legendDataBottom.push(this.legendData[i]);
          }
        }
      }
    });
    if (this.metricType === MetricType.NewPatientVisits) {
      this.benchmarkTypes =
        this.benchmarkTypes.filter(option => option.value !== BenchmarkType.Percentile65th);
    }

    this.hasBenchmarks = this.ngRedux.getState().display.viewCommunityBenchmarks ? this.hasCommunityBenchmarks : this.hasAcademicBenchmarks;

    updateCommunityBenchmarkAlertStatus(
      this.hasCommunityBenchmarks,
      this.hasAcademicBenchmarks,
      this.ngRedux,
      this.showProgressBar
    );
  }

  getBenchmarkHeaderClass(legend: LegendData): string {
    let headerClass = legend.benchmark ? 'legend-bchmk-header' : '';
    switch (this.metricType) {
      case MetricType.NewPatientVisits:
        headerClass = headerClass.concat(' npv-legend-header');
        break;
    }
    return headerClass;
  }

  updateBenchmarkType(value: BenchmarkType): void {
    this.setBenchmarkDropDownClass(value);
    this.ngRedux.dispatch(benchmarkChangedTo(value));
    this.analyticsService.handleGoogleAnalytics('Overview Benchmark', 'Benchmark Change', BenchmarkType[value]);
  }

  setBenchmarkDropDownClass(benchmark: BenchmarkType) {
    this.benchMarkDropDownClass = 'benchmark-mean';
    if (benchmark !== BenchmarkType.Mean) {
      this.benchMarkDropDownClass = 'benchmark-percentile';
    }
  }
}
